import type { CustomCellRendererProps } from "ag-grid-react";
import { clsx } from "clsx";
import { FitText } from "widgets";

import style from "../styles/Cell.module.css";
import { buildNumberCandidates } from "../utils/numberCandidates.ts";
import { extractValueAsArray } from "../utils/tables.ts";
import { ApproxText } from "./ApproxText.tsx";
import { NumberCell } from "./NumberCell.tsx";

export const NumberListCell = (props: CustomCellRendererProps) => {
  // We do flat at the moment for the support of nested list
  const values = extractValueAsArray(props.value)
    .flat()
    .filter((v) => v != null);

  if (values.length === 1) {
    return <NumberCell {...props} value={values[0]} />;
  }

  /**
   * For numeric lists we avoid truncating digits with an ellipsis. Instead, we:
   * - show the full list if it fits
   * - otherwise show as many full items as fit and collapse with `+N`
   * - if even the first item is too wide, fall back to the first item's own candidates (precision/compact/etc) + `+N`
   *
   * The tooltip always shows the full (most accurate) list representation.
   */
  const numberItems = values
    .filter((v) => v != null && v !== "")
    .map((v) => ({
      raw: typeof v === "string" ? v : undefined,
      numberValue: typeof v === "number" ? v : typeof v === "string" ? Number(v) : Number.NaN,
      fallbackText: String(v),
    }));

  const fullTexts = numberItems.map(({ numberValue, raw, fallbackText }) =>
    Number.isFinite(numberValue) ? (buildNumberCandidates(numberValue, raw)[0] ?? fallbackText) : fallbackText,
  );

  const fullListText = fullTexts.join(", ");
  const total = fullTexts.length;

  const candidates = buildNumberListCandidates(fullTexts, numberItems[0]?.raw, numberItems[0]?.numberValue, total);
  const renderText = (text: string) => <ApproxText text={text} />;

  return (
    <div className={clsx(style.cell, style.truncate_container, style.right_aligned)}>
      <span className={style.truncate_autosize} aria-hidden>{fullListText}</span>
      <FitText
        candidates={candidates}
        tooltipText={fullListText}
        renderText={renderText}
        className={clsx(style.truncate_content, style.right_aligned)}
      />
    </div>
  );
};

const buildNumberListCandidates = (
  fullTexts: readonly string[],
  firstRaw: string | undefined,
  firstNumberValue: number | undefined,
  total: number,
) => {
  const candidates: string[] = [];

  const push = (candidate: string) => {
    if (!candidate) return;
    if (candidates.includes(candidate)) return;
    candidates.push(candidate);
  };

  push(fullTexts.join(", "));

  for (let visibleCount = total - 1; visibleCount >= 2; visibleCount--) {
    const hiddenCount = total - visibleCount;
    push(fullTexts.slice(0, visibleCount).join(", ") + ` +${hiddenCount}`);
  }

  const hiddenAfterFirst = total - 1;
  if (hiddenAfterFirst > 0) {
    const firstText = fullTexts[0] ?? "";
    push(`${firstText} +${hiddenAfterFirst}`);

    if (typeof firstNumberValue === "number" && Number.isFinite(firstNumberValue)) {
      for (const candidate of buildNumberCandidates(firstNumberValue, firstRaw)) {
        push(`${candidate} +${hiddenAfterFirst}`);
      }
    }

    push(`+${total}`);
  }

  return candidates;
};
